/** Who is signed in, and what they may do (§3). */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roleId: string;
  roleName: string;
  permissions: string[];
  mustResetPassword: boolean;
}

interface LoginResponse {
  accessToken: string;
  user: SessionUser;
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    const me = await api<{ user: SessionUser }>('/auth/me');
    setUser(me.user);
  }, []);

  // A valid refresh cookie means the session survives a page reload.
  useEffect(() => {
    let cancelled = false;
    setUnauthenticatedHandler(() => setUser(null));

    void (async () => {
      try {
        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (response.ok) {
          const body = (await response.json()) as LoginResponse;
          setAccessToken(body.accessToken);
          if (!cancelled) setUser(body.user);
        }
      } catch {
        // No session to resume; the login page handles it.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setUnauthenticatedHandler(null);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const body = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    setAccessToken(body.accessToken);
    setUser(body.user);
    return body.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      refreshUser: loadMe,
      can: (...permissions: string[]) =>
        Boolean(user && permissions.some((key) => user.permissions.includes(key))),
    }),
    [user, loading, signIn, signOut, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
